import { Component } from '@theme/component';
import { isMobileBreakpoint, scrollBehavior } from '@theme/utilities';

/**
 * @typedef {Object} ShopTheLookRefs
 * @property {HTMLElement[]} [dots]
 * @property {HTMLElement[]} [items]
 */

/**
 * Shop the look: the photo dots and the product cards highlight each other (`is-active`).
 * Dots and items share `data-index`. Clicking a dot focuses the product link
 * (on phones it scrolls the product into view instead).
 * Each item block renders its own `[data-look-dot]` (a section can't read theme block settings);
 * they are moved onto the photo (`[data-look-stage]`) on connect and after re-renders.
 * @extends {Component<ShopTheLookRefs>}
 */
class ShopTheLook extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#mountDots();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#mountDots();
  }

  /**
   * Highlights the dot and item with this index.
   * @param {string} index
   */
  mark(index) {
    this.#mark(index);
  }

  /** Clears the highlight. */
  clear() {
    this.#mark(null);
  }

  /**
   * Highlights the product of the dot, then focuses it (desktop) or scrolls to it (phones).
   * @param {string} index
   */
  select(index) {
    const item = (this.refs.items || []).find((element) => element.dataset.index === index);
    const link = item?.querySelector('.product-card__title-link');
    this.#mark(index);
    if (!item || !(link instanceof HTMLElement)) return;
    if (isMobileBreakpoint()) item.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
    else link.focus();
  }

  #mountDots() {
    const stage = this.querySelector('[data-look-stage]');
    if (!stage) return;
    for (const dot of this.querySelectorAll('[data-look-dot]')) {
      if (!(dot instanceof HTMLElement) || dot.parentElement === stage) continue;
      // A re-rendered block brings a fresh copy of its dot: replace the stale one
      for (const old of stage.querySelectorAll('[data-look-dot]')) {
        if (old instanceof HTMLElement && old.dataset.index === dot.dataset.index) old.remove();
      }
      stage.append(dot);
    }
  }

  /** @param {string | null} index */
  #mark(index) {
    for (const dot of this.refs.dots || []) dot.classList.toggle('is-active', dot.dataset.index === index);
    for (const item of this.refs.items || []) item.classList.toggle('is-active', item.dataset.index === index);
  }
}

if (!customElements.get('shop-the-look')) {
  customElements.define('shop-the-look', ShopTheLook);
}
